import type { OrganizationSettingsInput, OrganizationSettingsView, UpdateOrganizationSettingsBody, WorkingCalendarInput } from "@gioia365/contracts";
import { italianNationalHolidays } from "@gioia365/domain/italian-holidays";
import type { Locale, MessageKey } from "@gioia365/i18n";
import { formatNumericDate } from "../../i18n/format";
import type { Translate } from "../../i18n/locale";
import { timeZoneName } from "./time-zones";

// La bozza di «Calendario, fuso e lingua» (piano M1b Task 10, D9): tutto ciò che la sezione modifica, in una forma
// comoda per la schermata (un giorno è «si lavora» più due orari, le chiusure sono righe con il loro motivo), e le
// funzioni pure che la legano alle impostazioni del server: dal salvato alla bozza e ritorno, i problemi che
// impediscono di salvare, ciò che cambia detto a parole e il corpo di una sola richiesta.

export const WEEKDAY_KEYS = ["1", "2", "3", "4", "5", "6", "7"] as const;
/** Il giorno della settimana come nel server: 1 = lunedì … 7 = domenica. */
export type WeekdayKey = (typeof WEEKDAY_KEYS)[number];

export type DayDraft = {
  open: boolean;
  /** «HH:mm». Un giorno chiuso li conserva: sono l'orario da proporre quando lo si riapre. */
  start: string;
  end: string;
  /** Come il server lo conta (domanda aperta 24): non si cambia da qui, si conserva. */
  endInclusive?: boolean;
};

export type ClosureDraft = {
  /** «YYYY-MM-DD». */
  date: string;
  /** Vuoto per una chiusura che il server ha senza motivo. */
  reason: string;
};

export type CalendarDraft = {
  timeZone: string;
  defaultLocale: Locale;
  weeklyHours: Record<WeekdayKey, DayDraft>;
  /** Per data, dalla più vicina. */
  closures: ClosureDraft[];
};

/** Il nome del giorno in tre forme per le frasi: «Lunedì», «il lunedì» (si lavora), «del lunedì» (l'orario). */
export const WEEKDAY_NAME = {
  "1": "calendar.weekdays.name.1",
  "2": "calendar.weekdays.name.2",
  "3": "calendar.weekdays.name.3",
  "4": "calendar.weekdays.name.4",
  "5": "calendar.weekdays.name.5",
  "6": "calendar.weekdays.name.6",
  "7": "calendar.weekdays.name.7",
} as const satisfies Record<WeekdayKey, MessageKey>;
const WEEKDAY_ON = {
  "1": "calendar.weekdays.on.1",
  "2": "calendar.weekdays.on.2",
  "3": "calendar.weekdays.on.3",
  "4": "calendar.weekdays.on.4",
  "5": "calendar.weekdays.on.5",
  "6": "calendar.weekdays.on.6",
  "7": "calendar.weekdays.on.7",
} as const satisfies Record<WeekdayKey, MessageKey>;
const WEEKDAY_OF = {
  "1": "calendar.weekdays.of.1",
  "2": "calendar.weekdays.of.2",
  "3": "calendar.weekdays.of.3",
  "4": "calendar.weekdays.of.4",
  "5": "calendar.weekdays.of.5",
  "6": "calendar.weekdays.of.6",
  "7": "calendar.weekdays.of.7",
} as const satisfies Record<WeekdayKey, MessageKey>;

// L'orario di partenza del dominio (`DEFAULT_WEEKLY_HOURS`, decisione 36 del 30 settembre 2026): 08:30–18:00 dal
// lunedì al venerdì, con la fine compresa. Il dominio intero porta nel bundle il dataset dei comuni (D5), quindi qui
// se ne tiene una copia, che un test confronta con l'originale.
const DEFAULT_START = "08:30";
const DEFAULT_END = "18:00";
const WORKING_BY_DEFAULT: ReadonlySet<WeekdayKey> = new Set(["1", "2", "3", "4", "5"]);

const minutesOf = (clock: string) => Number(clock.slice(0, 2)) * 60 + Number(clock.slice(3, 5));
const clockOf = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

function dayFrom(key: WeekdayKey, interval: { start: string; end: string; endInclusive?: boolean | undefined } | null | undefined): DayDraft {
  if (interval === undefined) {
    // Un giorno omesso vale l'orario di partenza del dominio.
    return WORKING_BY_DEFAULT.has(key)
      ? { open: true, start: DEFAULT_START, end: DEFAULT_END, endInclusive: true }
      : { open: false, start: DEFAULT_START, end: DEFAULT_END };
  }
  if (interval === null) return { open: false, start: DEFAULT_START, end: DEFAULT_END };
  return { open: true, start: interval.start, end: interval.end, ...(interval.endInclusive === undefined ? {} : { endInclusive: interval.endInclusive }) };
}

/** Le impostazioni salvate come bozza: ogni giorno detto per esteso (i giorni omessi prendono l'orario del dominio). */
export function draftFromSettings(settings: Pick<OrganizationSettingsView, "timeZone" | "defaultLocale" | "workingCalendar">): CalendarDraft {
  const { weeklyHours, closedDates, closureReasons } = settings.workingCalendar;
  return {
    timeZone: settings.timeZone,
    defaultLocale: settings.defaultLocale,
    weeklyHours: Object.fromEntries(WEEKDAY_KEYS.map((key) => [key, dayFrom(key, weeklyHours[key])])) as Record<WeekdayKey, DayDraft>,
    closures: [...new Set(closedDates)].sort().map((date) => ({ date, reason: closureReasons?.[date] ?? "" })),
  };
}

/** La bozza come la vuole il server: i giorni chiusi `null`, `endInclusive` com'era, i motivi soltanto se ci sono. */
export function settingsFromDraft(draft: CalendarDraft): OrganizationSettingsInput {
  const weeklyHours: WorkingCalendarInput["weeklyHours"] = {};
  for (const key of WEEKDAY_KEYS) {
    const day = draft.weeklyHours[key];
    weeklyHours[key] = day.open
      ? { start: day.start, end: day.end, ...(day.endInclusive === undefined ? {} : { endInclusive: day.endInclusive }) }
      : null;
  }
  const closures = [...draft.closures].sort((a, b) => a.date.localeCompare(b.date));
  const withReason = closures.filter((closure) => closure.reason.trim() !== "");
  return {
    timeZone: draft.timeZone,
    defaultLocale: draft.defaultLocale,
    workingCalendar: {
      weeklyHours,
      closedDates: closures.map((closure) => closure.date),
      ...(withReason.length > 0 ? { closureReasons: Object.fromEntries(withReason.map((closure) => [closure.date, closure.reason.trim()])) } : {}),
    },
  };
}

export type CalendarProblem = "no_open_day" | "end_before_start";

/** Ciò che impedisce di salvare, come lo controlla il server: un giorno aperto almeno, e la fine dopo l'inizio. */
export function calendarProblems(draft: CalendarDraft): CalendarProblem | null {
  const open = WEEKDAY_KEYS.map((key) => draft.weeklyHours[key]).filter((day) => day.open);
  if (open.length === 0) return "no_open_day";
  return open.some((day) => minutesOf(day.end) <= minutesOf(day.start)) ? "end_before_start" : null;
}

/** Gli orari da offrire a passi di mezz'ora; un valore salvato fuori dall'intervallo o fra due passi resta al suo posto. */
export function clockOptions(from: string, to: string, current: string): string[] {
  const values: string[] = [];
  for (let minutes = minutesOf(from); minutes <= minutesOf(to); minutes += 30) values.push(clockOf(minutes));
  if (!values.includes(current)) values.push(current);
  return values.sort((a, b) => minutesOf(a) - minutesOf(b));
}

/** Il giorno di oggi (`YYYY-MM-DD`) nel fuso dell'azienda; un fuso che il browser non conosce vale quello UTC. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  try {
    // Il formato svedese è già «anno-mese-giorno».
    return new Intl.DateTimeFormat("sv-SE", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

/** Le chiusure che il server tiene al massimo (`workingCalendarSchema`): un limite tecnico, non una regola di prodotto. */
export const MAX_CLOSURES = 1000;

// Il pulsante «Aggiungi le festività del …» guarda al più così lontano; in pratica si ferma molto prima, perché ogni
// anno ha almeno undici festività e le chiusure sono al più 1000.
const HOLIDAY_YEARS_AHEAD = 100;

export type HolidayOffer = { year: number; missing: ClosureDraft[] };

/**
 * Le festività nazionali che «Aggiungi le festività del …» mette nella bozza (richiesta del product owner del 1° ottobre
 * 2026, questione aperta 122): quelle del primo anno, da quello di oggi in poi, che ne ha almeno una non ancora chiusa,
 * da oggi compreso in avanti, ognuna con il suo nome come motivo. `null` soltanto se per cento anni sono già tutte chiuse.
 */
export function nextNationalHolidays(closures: readonly ClosureDraft[], today: string): HolidayOffer | null {
  const closed = new Set(closures.map((closure) => closure.date));
  const first = Number(today.slice(0, 4));
  for (let year = first; year <= first + HOLIDAY_YEARS_AHEAD; year += 1) {
    const missing = italianNationalHolidays(year)
      .filter((holiday) => holiday.date >= today && !closed.has(holiday.date))
      .map((holiday) => ({ date: holiday.date, reason: holiday.name }));
    if (missing.length > 0) return { year, missing };
  }
  return null;
}

/**
 * La bozza con le festività di `nextNationalHolidays` fra le chiusure, in ordine di data: una data già chiusa resta con
 * il suo motivo, anche vuoto. `"full"` se si passerebbero le chiusure che il server tiene; `null` se non c'è niente da
 * aggiungere.
 */
export function addNationalHolidays(draft: CalendarDraft, today: string): { draft: CalendarDraft; year: number; added: number } | "full" | null {
  const offer = nextNationalHolidays(draft.closures, today);
  if (!offer) return null;
  if (draft.closures.length + offer.missing.length > MAX_CLOSURES) return "full";
  const closures = [...draft.closures, ...offer.missing].sort((a, b) => a.date.localeCompare(b.date));
  return { draft: { ...draft, closures }, year: offer.year, added: offer.missing.length };
}

/** Ciò che cambia rispetto al salvato, a parole, nell'ordine: fuso, giorni, chiusure, lingua. Ogni frase è una modifica. */
export function describeCalendarChanges(saved: CalendarDraft, draft: CalendarDraft, t: Translate): string[] {
  const sentences: string[] = [];
  // Una chiusura è una data di calendario: resta quel giorno in ogni fuso, in numeri come in tutto il prodotto.
  const day = (iso: string) => formatNumericDate(draft.timeZone, iso);

  if (draft.timeZone !== saved.timeZone) {
    sentences.push(t("calendar.changes.zone", { after: timeZoneName(t, draft.timeZone), before: timeZoneName(t, saved.timeZone) }));
  }

  for (const key of WEEKDAY_KEYS) {
    const before = saved.weeklyHours[key];
    const after = draft.weeklyHours[key];
    if (after.open && !before.open) {
      sentences.push(t("calendar.changes.opens", { day: t(WEEKDAY_ON[key]), start: after.start, end: after.end }));
    } else if (!after.open && before.open) {
      sentences.push(t("calendar.changes.closes", { day: t(WEEKDAY_ON[key]) }));
    } else if (after.open && (after.start !== before.start || after.end !== before.end)) {
      sentences.push(t("calendar.changes.hours", { day: t(WEEKDAY_OF[key]), start: after.start, end: after.end }));
    }
  }

  const savedReasons = new Map(saved.closures.map((closure) => [closure.date, closure.reason]));
  const draftReasons = new Map(draft.closures.map((closure) => [closure.date, closure.reason]));
  for (const closure of draft.closures) {
    const was = savedReasons.get(closure.date);
    if (was === undefined) {
      sentences.push(
        closure.reason === ""
          ? t("calendar.changes.closureAddedNoReason", { date: day(closure.date) })
          : t("calendar.changes.closureAdded", { date: day(closure.date), reason: closure.reason }),
      );
    } else if (closure.reason !== "" && closure.reason !== was) {
      sentences.push(t("calendar.changes.closureReason", { date: day(closure.date), reason: closure.reason }));
    }
  }
  for (const closure of saved.closures) {
    if (draftReasons.has(closure.date)) continue;
    sentences.push(
      closure.reason === ""
        ? t("calendar.changes.closureRemovedNoReason", { date: day(closure.date) })
        : t("calendar.changes.closureRemoved", { date: day(closure.date), reason: closure.reason }),
    );
  }

  if (draft.defaultLocale !== saved.defaultLocale) {
    sentences.push(t("calendar.changes.language", { language: t(`calendar.changes.languages.${draft.defaultLocale}`) }));
  }
  return sentences;
}

/**
 * Il corpo di `PATCH /api/organization/settings`: il calendario intero (sostituisce il vecchio) se è cambiato
 * qualcosa, il fuso e la lingua soltanto se sono cambiati. Vuoto se non cambia niente.
 */
export function updateBodyFrom(saved: CalendarDraft, draft: CalendarDraft): UpdateOrganizationSettingsBody {
  const before = settingsFromDraft(saved);
  const next = settingsFromDraft(draft);
  return {
    ...(draft.timeZone === saved.timeZone ? {} : { timeZone: next.timeZone }),
    ...(draft.defaultLocale === saved.defaultLocale ? {} : { defaultLocale: next.defaultLocale }),
    // Le chiavi del calendario nascono sempre nello stesso ordine: il testo JSON basta a confrontarlo.
    ...(JSON.stringify(before.workingCalendar) === JSON.stringify(next.workingCalendar) ? {} : { workingCalendar: next.workingCalendar }),
  };
}
